import {
  createClient,
  SupabaseClient as OriginalSupabaseClient
} from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import type { Database } from './types/database_generated.types'

// Re-export SupabaseClient if other parts of your @stepcare/supabase package use it directly
export type SupabaseClient = OriginalSupabaseClient<Database>

let supabaseInstance: SupabaseClient | null = null

/**
 * Initializes and returns a Supabase client instance.
 * Ensures that the client is a singleton.
 * @param supabaseUrl The URL of your Supabase project.
 * @param supabaseAnonKey The anon key for your Supabase project.
 * @returns The initialized Supabase client.
 * @throws Error if URL or Key is missing.
 */
export const getSupabase = (
  supabaseUrl: string,
  supabaseAnonKey: string
): SupabaseClient => {
  if (!supabaseUrl) {
    throw new Error('Supabase URL is required.')
  }
  if (!supabaseAnonKey) {
    throw new Error('Supabase Anon Key is required.')
  }

  if (!supabaseInstance) {
    // Use implicit flow for Capacitor to avoid PKCE storage issues
    const isCapacitor = Capacitor.isNativePlatform()
    
    supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: isCapacitor ? 'implicit' : 'pkce', // Use implicit flow for Capacitor
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        storageKey: `sb-${supabaseUrl.split('//')[1]?.split('.')[0] || 'auth'}-auth-token`
      },
      global: {
        headers: {
          'X-Client-Info': 'stepcare-web'
        }
      }
    })
  }
  return supabaseInstance
}
